import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { newAuth, oldAuth, newDb, oldDb } from '@/firebase/firebaseInit';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import router from '@/router';
import { apiClient, API_PATHS } from '@/services/api';
import { accountSetupPendingError, isAccountSetupPendingError } from '@/utils/accountSetupPending';

const OLD_COLLECTION_NAME = 'rpp-users';
const NEW_COLLECTION_NAME = 'rp-users';

export const firebaseAuthService = {
  /**
   * Exposed so TwoFactorChallenge.vue can resolve a TOTP multi-factor sign-in.
   * Set by the login() method when auth/multi-factor-auth-required is thrown.
   */
  pendingMfaResolver: null,

  async login(email, password) {
    try {
      // First check if the user already exists in the new Firebase
      const existingNewUser = await this.checkUserExistsInNewFirebase(email);

      if (existingNewUser) {
        // If user exists in new Firebase, try logging in there first
        try {
          const newUserCredential = await signInWithEmailAndPassword(newAuth, email, password);
          const userData = await this.getUserData(
            newUserCredential.user.uid,
            newDb,
            NEW_COLLECTION_NAME,
          );
          return { userData, isNewFirebase: true };
        } catch (error) {
          if (error.code === 'auth/multi-factor-auth-required') {
            // Identity Platform TOTP MFA — surface the resolver
            this.pendingMfaResolver = getMultiFactorResolver(newAuth, error);
            throw {
              code: 'auth/multi-factor-required',
              message: 'mfa-required',
              resolver: this.pendingMfaResolver,
            };
          }
          console.error('New Firebase login failed:', error);
          throw this.handleAuthError(error);
        }
      } else {
        // Try old Firebase
        try {
          const oldUserCredential = await signInWithEmailAndPassword(oldAuth, email, password);
          const oldUserData = await this.getUserData(
            oldUserCredential.user.uid,
            oldDb,
            OLD_COLLECTION_NAME,
          );

          // Migrate user to new Firebase
          try {
            await this.migrateUser(oldUserCredential.user, oldUserData, email, password);

            // After migration, log in with new Firebase
            const newUserCredential = await signInWithEmailAndPassword(newAuth, email, password);
            const newUserData = await this.getUserData(
              newUserCredential.user.uid,
              newDb,
              NEW_COLLECTION_NAME,
            );

            return { userData: newUserData, isNewFirebase: true };
          } catch (migrationError) {
            // Terminal: never fall back to the old account's data.
            if (isAccountSetupPendingError(migrationError)) throw migrationError;
            console.error('Migration failed:', migrationError);
            // If migration fails, still return old user data
            return { userData: oldUserData, isNewFirebase: false };
          }
        } catch (oldAuthError) {
          console.error('Old Firebase login failed:', oldAuthError);
          throw this.handleAuthError(oldAuthError);
        }
      }
    } catch (error) {
      if (error.code === 'auth/multi-factor-required') throw error;
      console.error('Login process failed:', error);
      throw this.handleAuthError(error);
    }
  },

  /**
   * Resolve a TOTP MFA challenge using the code from the user's authenticator app.
   * Call this after login() throws auth/multi-factor-required.
   */
  async resolveTotp(totpCode) {
    if (!this.pendingMfaResolver) {
      throw { code: 'mfa/no-pending-resolver', message: 'No MFA challenge in progress.' };
    }
    try {
      const resolver = this.pendingMfaResolver;
      const totpHint = resolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      if (!totpHint) {
        throw { code: 'mfa/no-totp-hint', message: 'No TOTP factor enrolled.' };
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, totpCode);
      const userCredential = await resolver.resolveSignIn(assertion);
      this.pendingMfaResolver = null;

      const userData = await this.getUserData(
        userCredential.user.uid,
        newDb,
        NEW_COLLECTION_NAME,
      );
      return { userData, isNewFirebase: true };
    } catch (error) {
      if (error.code === 'auth/invalid-verification-code') {
        throw {
          code: error.code,
          message: 'The authenticator code is incorrect. Please try again.',
        };
      }
      throw error;
    }
  },

  async checkUserExistsInNewFirebase(email) {
    try {
      const usersRef = collection(newDb, NEW_COLLECTION_NAME);
      //console.log(newDb);
      console.log(NEW_COLLECTION_NAME);
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking user existence:', error);
      return false;
    }
  },

  async getUserData(uid, db, collectionName) {
    try {
      // For new Firebase users, use /auth/me endpoint for enriched data
      if (collectionName === NEW_COLLECTION_NAME) {
        try {
          const currentUser = newAuth.currentUser;
          if (currentUser) {
            console.log('[Auth] Fetching user data from /auth/me endpoint');
            const response = await apiClient.rfp.get(API_PATHS.rfp.auth.me);

            console.log('[Auth] User data fetched from /auth/me with partnerSettings');

            // Store partnerSettings in localStorage if present
            if (response.data.partnerSettings) {
              const existingUserData = JSON.parse(localStorage.getItem('userAddData') || '{}');
              const updatedUserData = {
                ...existingUserData,
                ...response.data,
                partnerSettings: response.data.partnerSettings,
              };
              localStorage.setItem('userAddData', JSON.stringify(updatedUserData));
              console.log(
                '[Auth] Partner settings stored in localStorage:',
                response.data.partnerSettings,
              );
            }

            return response.data; // Already includes partnerSettings
          }
        } catch (apiError) {
          // The backend refuses this user (shared fleet_onboarding_temp group): terminal,
          // no Firestore fallback, no session.
          if (isAccountSetupPendingError(apiError)) {
            await signOut(newAuth).catch(() => undefined);
            throw accountSetupPendingError();
          }
          console.warn(
            '[Auth] Failed to fetch from /auth/me, falling back to Firestore:',
            apiError,
          );
          // Fall through to Firestore fallback
        }
      }

      // Fallback: Direct Firestore read (for old Firebase or if API fails)
      const userRef = doc(db, collectionName, uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      return {
        ...userDoc.data(),
        uid: uid,
      };
    } catch (error) {
      if (isAccountSetupPendingError(error)) throw error;
      console.error(`Error getting user data from ${collectionName}:`, error);
      throw new Error('Error retrieving user data');
    }
  },

  async migrateUser(oldUser, oldUserData, email, password) {
    try {
      // First check if user already exists in new Firebase
      const existingUser = await this.checkUserExistsInNewFirebase(email);
      if (existingUser) {
        console.log('User already exists in new Firebase');
        return;
      }

      // Create user in new Firebase
      const newUserCredential = await createUserWithEmailAndPassword(newAuth, email, password);

      // Prepare user data for new Firestore
      const { createdAt, updatedAt, lastLoginAt, ...dataToMigrate } = oldUserData;

      // Remove any undefined or null values
      Object.keys(dataToMigrate).forEach((key) => {
        if (dataToMigrate[key] === undefined || dataToMigrate[key] === null) {
          delete dataToMigrate[key];
        }
      });

      let partnerTypes = ['API_PARTNER'];
      if (oldUserData && oldUserData.userGroup === 'ryd') {
        partnerTypes = ['API_PARTNER', 'FLEET_PARTNER', 'RYD_INTERNAL'];
      }

      // Add new user data
      const newUserData = {
        ...dataToMigrate,
        uid: newUserCredential.user.uid,
        email: email,
        migratedFrom: oldUser.uid,
        language: 'en',
        timezone: 'Europe/Berlin',
        partnerTypes: partnerTypes,
        migratedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
      };

      // Try to write to new Firestore
      try {
        const newUserRef = doc(newDb, NEW_COLLECTION_NAME, newUserCredential.user.uid);
        await setDoc(newUserRef, newUserData);
      } catch (firestoreError) {
        console.error('Firestore write failed:', firestoreError);
        // If Firestore write fails, delete the created auth user
        await newUserCredential.user.delete();
        throw firestoreError;
      }

      return newUserCredential.user;
    } catch (error) {
      console.error('Migration error:', error);
      throw new Error('Failed to migrate user: ' + error.message);
    }
  },

  handleAuthError(error) {
    let errorMessage = 'An error occurred during authentication.';

    switch (error.code) {
      case 'auth/user-disabled':
        errorMessage = 'Your account has been disabled. Please contact support for assistance.';
        break;
      case 'auth/wrong-password':
      case 'auth/invalid-login-credentials':
        errorMessage = 'Your email or password is incorrect.';
        break;
      case 'auth/user-not-found':
        errorMessage = 'User not found.';
        break;
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already registered.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address.';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/password accounts are not enabled. Please contact support.';
        break;
      case 'auth/weak-password':
        errorMessage = 'The password is too weak.';
        break;
      default:
        errorMessage = error.message;
    }

    return { code: error.code, message: errorMessage };
  },

  async getIdToken() {
    try {
      const currentUser = newAuth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      return await currentUser.getIdToken(true);
    } catch (error) {
      console.error('Error getting ID token:', error);
      throw error;
    }
  },

  async getCurrentUser() {
    try {
      const currentUser = newAuth.currentUser;
      if (!currentUser) {
        return null;
      }
      return currentUser;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },
};

export const enforceAuthRules = () => {
  onAuthStateChanged(newAuth, async (user) => {
    if (!user) return; // No user logged in

    try {
      console.log('onAuthStateChanged happened!');

      // Refresh token to ensure latest claims
      await user.getIdToken(true);

      // Fetch user status from Firestore
      const userRef = doc(newDb, 'rp-users', user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists() && userDoc.data().disabled) {
        console.warn('User is disabled. Forcing logout...');

        // Log out the user
        await signOut(newAuth);
        localStorage.removeItem('userAddData');

        // Redirect to login
        router.push('/auth/login');

        // Show notification
        alert('Your account has been disabled. Please contact support.');
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      // If token refresh fails due to being expired, revoked, or user not found, force a logout
      if (
        error.code === 'auth/user-token-expired' ||
        error.code === 'auth/user-not-found' ||
        (error.message && (error.message.includes('expired') || error.message.includes('revoked')))
      ) {
        console.warn('Session has been revoked or is invalid during auth state change. Forcing logout...');
        try {
          await signOut(newAuth);
          localStorage.removeItem('userAddData');
          const savedLanguage = localStorage.getItem('language');
          const query = { revoked: 'true' };
          if (savedLanguage) {
            query.lang = savedLanguage;
          }
          router.push({ path: '/auth/login', query });
        } catch (logoutError) {
          console.error('Error signing out:', logoutError);
        }
      }
    }
  });

  // Periodically check if the session has been revoked (e.g. every 1 minute)
  // This ensures that users browsing static pages (with no backend requests) are logged out
  // within 1 minute of their refresh token being revoked.
  setInterval(async () => {
    const user = newAuth.currentUser;
    if (user) {
      try {
        await user.getIdToken(true);
      } catch (error) {
        console.warn('Periodic check failed. Session has been revoked or is invalid. Logging out...', error);
        try {
          await signOut(newAuth);
          localStorage.removeItem('userAddData');
          const savedLanguage = localStorage.getItem('language');
          const query = { revoked: 'true' };
          if (savedLanguage) {
            query.lang = savedLanguage;
          }
          router.push({ path: '/auth/login', query });
        } catch (logoutError) {
          console.error('Error signing out during periodic check:', logoutError);
        }
      }
    }
  }, 60000);
};
